import React, { useMemo, useState } from 'react';

import { CaretDownOutlined } from '@ant-design/icons';
import { Dropdown, Empty, Input, Tree } from 'antd';
import type { AntTreeNodeProps, DataNode } from 'antd/es/tree';

import { ClearIcon } from '@/shared/assets/icons/input/ClearIcon';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { highlightText } from '@/shared/lib/text';
import { Icon } from '@/shared/ui/Icon';

import { useFilterStore } from '@/entities/discovery';
import type { FilterOption } from '@/entities/discovery';

import { useDataSourceOptions } from '@/features/discovery';

import styles from './DataSourceDropdown.module.css';

export const DataSourceDropdown: React.FC = () => {
  const { filters } = useFilterStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selectedKeys = useMemo(() => filters.dataSource || [], [filters.dataSource]);

  const { options: dataSourceOptions } = useDataSourceOptions(selectedKeys);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return dataSourceOptions;

    const filterTree = (options: FilterOption[]): FilterOption[] => {
      return options
        .map((option) => {
          const matchesTitle = option.title.toLowerCase().includes(searchQuery.toLowerCase());
          const filteredChildren = option.children ? filterTree(option.children) : [];

          if (matchesTitle || filteredChildren.length > 0) {
            return {
              ...option,
              children: filteredChildren.length > 0 ? filteredChildren : option.children,
            };
          }
          return null;
        })
        .filter(Boolean) as FilterOption[];
    };

    return filterTree(dataSourceOptions);
  }, [searchQuery, dataSourceOptions]);

  const treeData = useMemo(() => {
    const convertToTreeData = (options: FilterOption[]): DataNode[] => {
      return options.map((option) => ({
        title: (
          <div className={styles.treeNodeTitle}>
            <span className={styles.nodeLabel}>
              {highlightText(option.title, searchQuery, { className: 'highlight' })}
            </span>
          </div>
        ),
        key: option.id,
        children: option.children ? convertToTreeData(option.children) : [],
        disabled: option.disabled,
        className: option.disabled ? styles.disabledNode : undefined,
      }));
    };
    return convertToTreeData(filteredOptions);
  }, [filteredOptions, searchQuery]);

  const handleCheck = (checkedKeys: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
    const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    const stringKeys = keys.map((key) => String(key));

    const store = useFilterStore.getState();
    store.updateDataSourceFilter(stringKeys, 'topBar');
    store.updateQuickFilter(undefined);
  };

  const getAllChildKeys = (option: FilterOption): string[] => {
    const keys: string[] = [];
    if (option.children) {
      option.children.forEach((child) => {
        keys.push(child.id);
        keys.push(...getAllChildKeys(child));
      });
    }
    return keys;
  };

  const getParentKey = (childKey: string): string | null => {
    for (const option of dataSourceOptions) {
      if (option.children) {
        const hasChild = option.children.some((child) => child.id === childKey);
        if (hasChild) {
          return option.id;
        }
      }
    }
    return null;
  };

  const handleSelect = (_keys: React.Key[], info: { node: { key: React.Key } }) => {
    const clickedKey = String(info.node.key);
    const currentCheckedSet = new Set(selectedKeys);
    let newKeys = [...selectedKeys];

    if (currentCheckedSet.has(clickedKey)) {
      const clickedOption = dataSourceOptions
        .flatMap((opt) => [opt, ...(opt.children || [])])
        .find((opt) => opt.id === clickedKey);

      if (clickedOption) {
        const keysToRemove = new Set([clickedKey, ...getAllChildKeys(clickedOption)]);
        newKeys = newKeys.filter((k) => !keysToRemove.has(k));

        const parentKey = getParentKey(clickedKey);
        if (parentKey) {
          const parent = dataSourceOptions.find((opt) => opt.id === parentKey);
          if (parent?.children) {
            const allChildrenUnchecked = parent.children.every((child) => !newKeys.includes(child.id));
            if (allChildrenUnchecked) {
              newKeys = newKeys.filter((k) => k !== parentKey);
            }
          }
        }
      }
    } else {
      const clickedOption = dataSourceOptions
        .flatMap((opt) => [opt, ...(opt.children || [])])
        .find((opt) => opt.id === clickedKey);

      if (clickedOption) {
        const keysToAdd = [clickedKey, ...getAllChildKeys(clickedOption)];
        const newKeysSet = new Set([...newKeys, ...keysToAdd]);
        newKeys = Array.from(newKeysSet);
      }
    }

    useFilterStore.getState().updateDataSourceFilter(newKeys, 'topBar');
  };

  const handleClear = () => {
    useFilterStore.getState().updateDataSourceFilter([], 'topBar');
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setSearchQuery('');
    }
  };

  const getButtonText = () => {
    if (selectedKeys.length === 0) return '전체';

    const displayKeys = new Set(selectedKeys);

    dataSourceOptions.forEach((parent) => {
      if (parent.children && parent.children.length > 0) {
        const parentSelected = displayKeys.has(parent.id);
        const allChildrenSelected = parent.children.every((child) => displayKeys.has(child.id));

        if (parentSelected && allChildrenSelected) {
          parent.children.forEach((child) => displayKeys.delete(child.id));
        }
      }
    });

    const displayKeysArray = Array.from(displayKeys);

    if (displayKeysArray.length === 0) return '';

    const allOptions = dataSourceOptions.flatMap((opt) => [opt, ...(opt.children || [])]);

    if (displayKeysArray.length === 1) {
      const firstKey = displayKeysArray[0];
      const firstOption = allOptions.find((opt) => opt.id === firstKey);
      return firstOption?.title;
    }

    const firstKey = displayKeysArray[0];
    const firstOption = allOptions.find((opt) => opt.id === firstKey);
    const firstLabel = firstOption?.title || displayKeysArray[0];
    const remainingCount = displayKeysArray.length - 1;

    return `${firstLabel}, ... +${remainingCount}`;
  };

  const switcherIcon = (nodeProps: AntTreeNodeProps) => {
    const { expanded } = nodeProps;
    return expanded ? (
      <Icon name="tree-collapse" size={16} color={COLOR.GRAY[0]} />
    ) : (
      <Icon name="tree-expand" size={16} color={COLOR.GRAY[0]} />
    );
  };

  const dropdownContent = (
    <div className={styles.dropdownContent}>
      <div className={styles.searchSection}>
        <Input
          prefix={<Icon name="search" size={13} color={COLOR.GRAY[0]} />}
          placeholder="데이터 서비스 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          maxLength={256}
          allowClear={{
            clearIcon: <ClearIcon size={14} backgroundColor={COLOR.GRAY[80]} iconColor={COLOR.GRAY[100]} />,
          }}
          className={styles.searchInput}
        />
      </div>

      {treeData.length > 0 ? (
        <div className={styles.treeSection}>
          <Tree
            checkable
            checkedKeys={selectedKeys}
            selectedKeys={[]}
            onCheck={handleCheck}
            onSelect={handleSelect}
            treeData={treeData}
            switcherIcon={switcherIcon}
            className={styles.tree}
          />
        </div>
      ) : (
        <div className={styles.emptyWrapper}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다` : '검색 결과가 없습니다'}
          />
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.clearButton} ${selectedKeys.length === 0 ? styles.inactive : ''}`}
          onClick={handleClear}>
          선택 해제
        </button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomLeft"
      align={{ offset: [0, 8] }}>
      <button type="button" className={styles.filterButton} onClick={() => setOpen(!open)}>
        <div className={styles.buttonContent}>
          <span className={styles.buttonLabel}>데이터 서비스:</span>
          <span className={styles.buttonValue}>{getButtonText()}</span>
        </div>
        <span className={styles.iconWrapper}>
          <CaretDownOutlined className={styles.dropdownIcon} />
        </span>
      </button>
    </Dropdown>
  );
};
